import { Link, useParams } from 'react-router-dom'

import { t } from '../i18n'
import { Notice } from '../ui'

/**
 * What an address under a project draws when it names no screen.
 *
 * The shell's child routes are all fully pathed, so until this existed a path
 * under `/p/<id>/` that matched none of them matched nothing inside the shell
 * and fell through to the application's own last route, which sends the
 * browser to `/`. That redirect does not know a project id was in the address
 * it discarded: somebody who mistyped a screen on the Riverbend job is taken
 * off it, with nothing on the screen or in the address bar saying either that
 * the address was wrong or that the job had closed.
 *
 * `/` is the chooser (#254), so what they land on is their own list. It was a
 * redirect to the first job they held when this was written, which put them on
 * the Northgate overview instead — a worse version of the same discard, and
 * the reason the repair reads the way it does.
 *
 * Being a child of the shell is the whole repair. The project in the URL is
 * resolved by the shell above, so the person stays on the job they asked for,
 * with its navigation beside them, and the page says the one true thing: this
 * address does not exist.
 *
 * It is careful not to imply a refusal. Nothing was denied here — the person
 * holds this project and every level they held a moment ago — and a page that
 * read as "no access" would send them to ask an administrator for something
 * they already have.
 */
export function UnknownAddress() {
  const { projectId } = useParams<{ projectId: string }>()
  return (
    <>
      <h2 className="page">{t('app.unknown_address')}</h2>
      <Notice tone="warn">
        <div className="muted">{t('app.unknown_address_body')}</div>
      </Notice>
      <p>
        <Link to={`/p/${projectId ?? ''}`}>{t('app.unknown_address_back')}</Link>
      </p>
    </>
  )
}
